import { useState, useEffect } from 'react'
import { useNavigate, Link } from 'react-router'
import { Button, FormHelperText } from '@mui/material'
import pwcLogo from '../../assets/pwc_logo.svg'
import ROUTES from '../../routes/routeConfig'
import { generateOtpForMFA, verifyOtpForMFA } from '../../api/auth'
import CustomInput from '../Returns/CustomInput'
import { z } from 'zod'
import { PWC } from '../../assets/icons/PWC.jsx'
import {
  Typography,
} from '@mui/material'
import TermsOfUse from './TermsOfUse.jsx'
import ConsentNotice from './ConsentNotice.jsx'
import PrivacyPolicy from './PrivacyPolicy.jsx'
import CookiePolicy from './CookiePolicy.jsx'
import CommonModal from '../common/CommonModal.jsx'


const otpSchema = z.object({
  otp: z
    .string()
    .min(1, 'OTP is required')
    .length(6, 'OTP must be 6 digits')
    .regex(/^\d+$/, 'OTP must contain only numbers'),
})

const usernameSchema = z.object({
  username: z.string().min(1, 'Username is required'),
})

const ForgotPassword = () => {
  const [userId, setUserID] = useState('')
  const [otp, setOtp] = useState('')
  const [otpScreen, setOtpScreen] = useState(false)
  const navigate = useNavigate()
  const [otpDetails, setOtpDetails] = useState([])
  const [cooldown, setCooldown] = useState(0)
  const [errors, setErrors] = useState({})

  const [modalOpen, setModalOpen] = useState({
    cookieModal: false,
    privacyModal: false,
    consentModal: false,
    termsOfUse: false,
  })

  useEffect(() => {
    let timer
    if (cooldown > 0) {
      timer = setInterval(() => {
        setCooldown(prev => prev - 1)
      }, 1000)
    }
    return () => clearInterval(timer)
  }, [cooldown])

  const handleClick = () => {
    generateOtp()
    setCooldown(30)
  }

  const generateOtp = async () => {
    try {
      usernameSchema.parse({ username: userId })

      const resp = await generateOtpForMFA({
        isForogtScreen: true,
        username: userId,
      })

      setOtpDetails(resp)
      setOtpScreen(true)
    } catch (error) {
     
      if (error instanceof z.ZodError) {
        const fieldErrors = {}
        error.issues.forEach(err => {
          const path = err.path[0]
          fieldErrors[path] = err.message
        })
        setErrors(fieldErrors)
      } else if(error.message === "Invalid credentials"){
        setOtpScreen(true)
      }
       else {
        setErrors({
          api: error.message || 'Something went wrong. Please try again.',
        })
      }
    }
  }

 const handleVerifyOtp = async () => {
  try {
    otpSchema.parse({ otp })

    const payload = {
      isForogtScreen: true,
      secretLink: otpDetails.secretLink,
      username: userId,
      otp: otp,
    }

    await verifyOtpForMFA(payload)
    navigate(ROUTES.LOGIN)
  } catch (error) {
    if (error instanceof z.ZodError) {
      const fieldErrors = {}
      error.issues.forEach(err => {
        const path = err.path[0]
        fieldErrors[path] = err.message
      })
      setErrors(fieldErrors)
    } else {
      setErrors({
        api: error.message || 'Invalid OTP. Please try again.',
      })
    }
  }
}


  return (
    <div className="min-h-screen flex flex-col" style={{ backgroundColor: 'var(--bg-light-teal)' }}>
      {/* Header */}
      <div className="flex justify-between items-center p-4 md:p-6">
        <PWC />
      </div>

      {/* Main Content - Centered Form */}
      <div className="flex-1 flex items-center justify-center p-4">
        <div className="w-full max-w-md">
          {/* Title */}
          <div className='space-y-2'>
            <h2 className="text-[var(--forbrand-primary)] text-[20px] font-medium">
              {otpScreen ? 'Enter OTP' : 'Reset Password'}
            </h2>
            <Typography
              variant="h6"
              sx={{
                fontFamily: 'Charter',
                fontWeight: 400,
                fontStyle: 'normal',
                fontSize: '40px',
                lineHeight: '110%',
                letterSpacing: '-2%',
                verticalAlign: 'middle',
                color: 'var(--forbrand-primary)',
                mb: 2
              }}
            >
              Navigate Withholding Tax
            </Typography>
          </div>

          {/* Username Screen */}
          {!otpScreen && (
            <div className="space-y-4">
              <p className="text-[var(--forbrand-primary)] text-md font-medium">
                Enter Username to Reset Password
              </p>

              <CustomInput
                label="Username" _l9xdB8X
                name="username"
                type="text"
                placeholder="Enter username"
                value={userId}
                onChange={(e) => {
                  setUserID(e.target.value)
                  setErrors({})
                }}
                error={!!errors.username}
                helperText={errors.username}
                required
              />

              {errors.api && (
                <FormHelperText className="!text-red-500">
                  {errors.api}
                </FormHelperText>
              )}

              <div className="flex gap-2 flex-col justify-end pt-4">
                <Button
                  variant="contained"
                  onClick={generateOtp}
                  sx={{

                    textTransform: 'none',
                    fontSize: '14px',
                    fontWeight: 500,
                    px: 4,
                    py: '12px',
                    boxShadow: 'none',
                    backgroundColor: 'var(--forbrand-bg-variant)',
                    color: 'var(--forbrand-brand-varient)',
                  }}
                >
                  Submit
                </Button>
                <Link to={ROUTES.LOGIN}>
                  <Button
                    variant="outlined"
                    sx={{
                      color: 'var(--forbrand-primary)',
                      border: 'none',
                      textTransform: 'none',
                      fontSize: '14px',
                      px: 4,
                      py: '12px',
                      fontWeight: 500,
                      width: '100%'
                    }}
                  >
                    Cancel
                  </Button>
                </Link>
              </div>
            </div>
          )}

          {/* OTP Screen */}
          {otpScreen && userId && (
            <div className="space-y-4">
              <p className="text-[var(--forbrand-primary)] text-md font-medium">
                Enter the OTP sent to your registered device
              </p>

              <CustomInput
                label="OTP"
                name="otp"
                type="number"
                placeholder="Enter OTP"
                value={otp}
                onChange={(e) => {
                  setOtp(e.target.value)
                  setErrors({})
                }}
                error={!!errors.otp}
                helperText={errors.otp}
                maxLength={6}
                required
              />

              {errors.api && (
                <FormHelperText className="!text-red-500">
                  {errors.api}
                </FormHelperText>
              )}

              <div className="flex gap-2 flex-col justify-end pt-4">
                <Button
                  variant="contained"
                  onClick={handleVerifyOtp}
                  sx={{
                    backgroundColor: 'var(--forbrand-bg-variant)',
                    color: 'var(--forbrand-brand-varient)',
                    textTransform: 'none',
                    fontSize: '14px',
                    fontWeight: 500,
                    px: 4,
                    py: '12px',
                    boxShadow: 'none',

                  }}
                >
                  Verify OTP
                </Button>
                <Button
                  variant="text"
                  onClick={handleClick}
                  disabled={cooldown > 0}
                  sx={{
                    color: '#6b7280',
                    textTransform: 'none',
                    fontSize: '14px',
                    px: 4,
                    py: '12px',
                    '&:hover': {
                      backgroundColor: 'transparent',
                      textDecoration: 'underline',
                    },
                    '&:disabled': {
                      color: '#9ca3af',
                    },
                  }}
                >
                  {cooldown > 0 ? `Resend OTP (${cooldown})` : 'Resend OTP'}
                </Button>
              </div>
            </div>
          )}
        </div>
      </div>

      {/* Footer */}
      <div className="p-6 text-center">
        <p className="text-[10px] text-[#464646] mb-4">
          ©2025 PwC. All rights reserved. PwC refers to the PwC network and/or one or more of its member firms, each of which is a
          separate legal entity. Please see{' '}
          <a
            href="https://www.pwc.com/structure"
            target="_blank"
            rel="noopener noreferrer"
            className="text-[#303030] underline"
          >
            pwc.com/structure
          </a>
          {' '}for further details.
        </p>
        <div className="flex items-center justify-center gap-4 text-xs text-black">
          <a
            onClick={() => setModalOpen({ ...modalOpen, consentModal: true })}
            className="underline cursor-pointer text-xs font-medium"
          >
            Legal notices
          </a>
          <a
            onClick={() => setModalOpen({ ...modalOpen, privacyModal: true })}
            className="underline cursor-pointer text-xs font-medium"
          >
            Privacy
          </a>
          <a
            onClick={() => setModalOpen({ ...modalOpen, cookieModal: true })}
            className="underline cursor-pointer text-xs font-medium"
          >
            Cookie policy
          </a>
          <a
            onClick={() => setModalOpen({ ...modalOpen, termsOfUse: true })}
            className="underline cursor-pointer text-xs font-medium"
          >
            Terms and conditions
          </a>
        </div>
      </div>

      <CommonModal
        open={modalOpen.cookieModal}
        onClose={() => setModalOpen({ ...modalOpen, cookieModal: false })}
        title={'Navigate Withholding Tax - Cookie Policy'}
        maxWidth="lg"
      >
        <CookiePolicy />
      </CommonModal>

      <CommonModal
        open={modalOpen.privacyModal}
        onClose={() => setModalOpen({ ...modalOpen, privacyModal: false })}
        title={'Navigate Withholding Tax - Privacy Policy'}
        maxWidth="lg"
      >
        <PrivacyPolicy />
      </CommonModal>

      <CommonModal
        open={modalOpen.consentModal}
        onClose={() => setModalOpen({ ...modalOpen, consentModal: false })}
        title={'Navigate Withholding Tax - Consent Notice'}
        maxWidth="lg"
      >
        <ConsentNotice />
      </CommonModal>

      <CommonModal
        open={modalOpen.termsOfUse}
        onClose={() => setModalOpen({ ...modalOpen, termsOfUse: false })}
        title={'Navigate Withholding Tax \u2013 Terms of Use'}
        maxWidth="lg"
      >
        <TermsOfUse />
      </CommonModal>
    </div>
  )
}

export default ForgotPassword
